// per specs/060-agreed-alerts/plan.md C4, tasks T020 and T025
// The register of watched medicine groups under "Alerte și notificări": the set of monitored
// medicines exists once, and A4, A5 and A10 refer to it (caiet TF-127, TF-128). System
// Administrator only; the route belongs to the alerts entry (access-model SEGMENT_ALIASES).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import { MudButton, MudIcon, MudInfoBox, MudTable, MudTag } from '@egov-moldova/mud-react';
import {
  EMPTY_CELL,
  EmptyRegisterMessage,
  MetadataLine,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { apiErrorKey } from '../../app/api-client';
import { AlertsSectionNav } from '../../components/AlertsSectionNav';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { QueryState } from '../../components/QueryState/QueryState';
import { thresholdLabel } from './watched-groups-domain';
import {
  deleteWatchedGroup,
  listWatchedGroups,
  WATCHED_GROUPS_KEY,
  type WatchedGroup,
} from './watched-groups-api';

export function WatchedGroupsPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const [actionErrorKey, setActionErrorKey] = useState<string | null>(null);

  const query = useQuery({ queryKey: [WATCHED_GROUPS_KEY], queryFn: listWatchedGroups });
  const rows = query.data ?? [];

  const remove = useMutation({
    mutationFn: (group: WatchedGroup) => deleteWatchedGroup(group.id),
    onSuccess: (_, group) => {
      setActionErrorKey(null);
      void queryClient.invalidateQueries({ queryKey: [WATCHED_GROUPS_KEY] });
      toast({
        variant: 'success',
        title: t('watched_groups.toast.deleted'),
        text: group.name,
        closeLabel: t('common.close'),
      });
    },
    // 409 WATCHED_GROUP_IN_USE reaches the administrator as its own sentence: an active rule
    // still refers to the group and the delete is refused.
    onError: (err: unknown) =>
      setActionErrorKey(apiErrorKey(err, 'watched_groups.error.delete_failed')),
  });

  async function askThenDelete(group: WatchedGroup): Promise<void> {
    const confirmed = await confirm({
      title: t('watched_groups.confirm.delete.title'),
      body: <p>{t('watched_groups.confirm.delete.body', { name: group.name })}</p>,
      confirmLabel: t('watched_groups.action.delete'),
      cancelLabel: t('alerts.confirm.action.cancel'),
      destructive: true,
    });
    if (!confirmed) return;
    setActionErrorKey(null);
    remove.mutate(group);
  }

  const editHref = (group: WatchedGroup): string => `/system-admin/watched-groups/${group.id}/edit`;
  const historyHref = (group: WatchedGroup): string =>
    `/system-admin/watched-groups/${group.id}/history`;

  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '76px' },
    { key: 'group', label: t('watched_groups.column.group') },
    { key: 'threshold', label: t('watched_groups.column.threshold'), width: '20%' },
    { key: 'members', label: t('watched_groups.column.members'), width: '14%' },
    { key: 'status', label: t('watched_groups.column.status'), width: '14%' },
  ];
  const tableRows: TableRowData[] = rows.map((group) => ({ id: group.id }));

  const register = (
    <>
      <div className="simsm-alerts__register">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="zebra"
          disableSort={true}
          aria-label={t('alerts.section.watched_groups')}
          hoverable
          onMudRowClick={onRowOpen((index) => {
            const group = rows[index];
            if (group) navigate(editHref(group));
          })}
        >
          <EmptyRegisterMessage>{t('watched_groups.empty')}</EmptyRegisterMessage>
          <span slot="header-cell-actions" className="visually-hidden">
            {t('alerts.column.actions')}
          </span>
          {rows.map((group, index) => {
            const muted = group.active ? '' : ' simsm-alerts__cell--muted';
            return [
              <span
                key={`actions-${group.id}`}
                slot={`cell-actions-${index}`}
                className="simsm-alerts__cell simsm-alerts__cell--actions"
              >
                <RowActionsMenu
                  label={t('watched_groups.action.row_menu', { name: group.name })}
                  actions={[
                    {
                      label: t('watched_groups.action.edit'),
                      onSelect: () => navigate(editHref(group)),
                    },
                    {
                      label: t('watched_groups.action.history'),
                      onSelect: () => navigate(historyHref(group)),
                    },
                    {
                      label: t('watched_groups.action.delete'),
                      danger: true,
                      separator: true,
                      disabled: remove.isPending,
                      onSelect: () => {
                        void askThenDelete(group);
                      },
                    },
                  ]}
                />
              </span>,
              <span
                key={`group-${group.id}`}
                slot={`cell-group-${index}`}
                className={`simsm-alerts__cell-name${muted}`}
              >
                <RouterLink to={editHref(group)} size="sm" variant="strict" underline="hover">
                  {group.name}
                </RouterLink>
                {group.description ? (
                  <MetadataLine
                    wrap
                    items={[
                      {
                        label: t('watched_groups.column.description'),
                        value: group.description,
                        title: group.description,
                      },
                    ]}
                  />
                ) : null}
              </span>,
              <span
                key={`threshold-${group.id}`}
                slot={`cell-threshold-${index}`}
                className={`simsm-alerts__cell${muted}`}
              >
                {group.thresholdKind === null ? EMPTY_CELL : thresholdLabel(group, t)}
              </span>,
              <span
                key={`members-${group.id}`}
                slot={`cell-members-${index}`}
                className={`simsm-alerts__cell${muted}`}
              >
                {group.memberCount}
              </span>,
              <span
                key={`status-${group.id}`}
                slot={`cell-status-${index}`}
                className={`simsm-alerts__cell${muted}`}
              >
                <MudTag
                  variant="status"
                  type="outlined"
                  size="sm"
                  semantic={group.active ? 'success' : 'neutral'}
                  label={
                    group.active
                      ? t('watched_groups.status.active')
                      : t('watched_groups.status.inactive')
                  }
                />
              </span>,
            ];
          })}
        </MudTable>
      </div>
      <div className="simsm-alerts__table-footer">
        <span className="simsm-alerts__muted-text">
          {t(rows.length === 1 ? 'watched_groups.count_one' : 'watched_groups.count_other', {
            count: rows.length,
          })}
        </span>
      </div>
    </>
  );

  return (
    <section className="simsm-page simsm-alerts">
      <PageHeader
        title={t('alerts.section_title')}
        lede={t('alerts.section_lede')}
        actions={
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            onClick={() => navigate('/system-admin/watched-groups/new')}
          >
            <MudIcon slot="icon-start" name="plus-large" size={16} />
            {t('watched_groups.action.new')}
          </MudButton>
        }
      />

      <AlertsSectionNav />

      <p className="simsm-alerts__lede">{t('watched_groups.lede')}</p>

      {actionErrorKey ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          closable
          closeLabel={t('common.close')}
          onMudClose={() => setActionErrorKey(null)}
        >
          {t(actionErrorKey)}
        </MudInfoBox>
      ) : null}

      <QueryState query={query} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>
    </section>
  );
}
